import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { can, isFirmRole, strongest } from '@domain';
import type { LineAmountType, ReferenceData, TenantConnection } from '@domain';
import {
  loadConnections,
  loadList,
  loadLog,
  loadQueue,
  loadReference,
  createdContactId,
  createdInvoice,
  createdItemCode,
  createSignIn,
  invitePerson,
  setPersonPassword,
  loadDashboard,
  loadPeople,
  loadSession,
  setOrganisationFeatures,
  connectUrl,
  loadTrainingSetup,
  loadUnaccountedBills,
  saveDirectCostAccounts,
  type TrainingSetup,
  type UnaccountedBills,
  loadBranding,
  loadPaymentMethods,
  loadConnectionDetail,
  loadPaymentOptions,
  loadSummary,
  loadAged,
  invoiceCohort,
  linkCost,
  linkCredit,
  unlinkCredit,
  setCourseItem,
  setDeliveryClosed,
  unlinkCost,
  loadDelivery,
  loadProfitAndLoss,
  loadBalanceSheet,
  loadAgedDetail,
  loadTraining,
  saveCourse,
  saveDelivery,
  saveRegistration,
  type CourseInput,
  type DeliveryDetail,
  type DeliveryInput,
  type RegistrationInput,
  type TrainingOverview,
  recordMoney,
  recordPayment,
  type AgedResponse,
  type MoneyEntry,
  type XeroReportResponse,
  removeLogo,
  saveBranding,
  savePaymentMethods,
  type PaymentMethodSettings,
  type BrandingFooter,
  type Presentation,
  uploadLogo,
  type Branding,
  type PaymentOptions,
  type ConnectionDetail,
  type SummaryResponse,
  revokePerson,
  type PeopleResponse,
  type InvitableRole,
  NotSignedInError,
  pushDocument,
  XeroRejectedError,
  setActiveOrganisation,
  type Session,
} from './api.js';
import {
  beginSignIn,
  clearSignInAttempt,
  loadPrincipal,
  signInWasAttempted,
  SIGNED_OUT_PATH,
  SIGN_IN_FAILED_PATH,
} from './auth.js';
import type { DashboardCounts } from './api.js';
import { readLang, writeLang } from './lang.js';
import { DemoDataBanner } from './components/DemoDataBanner.js';
import { GlanceDrawer } from './components/GlanceDrawer.js';
import { Header } from './components/Header.js';
import { Sidebar } from './components/Sidebar.js';
import { FORMS } from './data/forms.js';
import { createTranslate, isRtl, type Lang } from './i18n/index.js';
import { createMessageRenderer } from './i18n/messages.js';
import { Dashboard } from './screens/Dashboard.js';
import { ConnectionsScreen, XeroScreen } from './screens/ConnectionsScreen.js';
import { FormScreen } from './screens/FormScreen.js';
import { ListScreen } from './screens/ListScreen.js';
import { LogScreen, QueueScreen } from './screens/QueueScreen.js';
import { AccessScreen, WaitingScreen, type AccessVariant } from './screens/AccessScreen.js';
import { NoAccessScreen } from './screens/NoAccessScreen.js';
import { PeopleScreen } from './screens/PeopleScreen.js';
import { BrandingScreen } from './screens/BrandingScreen.js';
import { DocumentScreen } from './screens/DocumentScreen.js';
import { ImportContactsScreen } from './screens/ImportContactsScreen.js';
import { PaymentMethodsScreen } from './screens/PaymentMethodsScreen.js';
import { MoneyScreen, type MoneyTab } from './screens/MoneyScreen.js';
import { PrintDocument } from './screens/PrintDocument.js';
import { presets, SummaryScreen, type DateRange } from './screens/SummaryScreen.js';
import { ReportsScreen, REPORT_TITLES } from './screens/ReportsScreen.js';
import { CoursesScreen } from './screens/CoursesScreen.js';
import { TrainingSetupScreen } from './screens/TrainingSetupScreen.js';
import { DeliveriesScreen } from './screens/DeliveriesScreen.js';
import { DeliveryScreen } from './screens/DeliveryScreen.js';
import {
  effectiveScreen,
  formKindOf,
  initialState,
  listKeyOf,
  reducer,
  reportKindOf,
  reportContactOf,
  trainingViewOf,
  viewRoleFor,
} from './state.js';
import { theme } from './theme.js';
import type { ListFixture } from './types.js';

const EMPTY_REFERENCE: ReferenceData = {
  accounts: [],
  taxRates: [],
  items: [],
  currencies: [],
  contacts: [],
};

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [reference, setReference] = useState<ReferenceData>(EMPTY_REFERENCE);
  const [knownReferences, setKnownReferences] = useState<string[]>([]);
  const [dashboardCounts, setDashboardCounts] = useState<DashboardCounts | null>(null);
  /** When this company's reference data was actually fetched. */
  const [referenceLoadedAt, setReferenceLoadedAt] = useState<number | null>(null);
  const [connections, setConnections] = useState<TenantConnection[]>([]);
  const [lists, setLists] = useState<Record<string, ListFixture>>({});
  const [state, dispatch] = useReducer(reducer, initialState([], []));

  /**
   * Which of the four things the browser is showing: nothing yet, one of the
   * three screens for someone who is not inside the application, or the
   * application itself.
   */
  // This month, recomputed on load rather than stored: a tab left open across
  // the turn of the month must not keep answering for the old one.
  const [range, setRange] = useState<DateRange>(() => presets()[0]!.range);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [connectionDetail, setConnectionDetail] = useState<ConnectionDetail | null>(null);
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [featuresBusy, setFeaturesBusy] = useState(false);
  const [featuresError, setFeaturesError] = useState<string | null>(null);
  const [trainingSetup, setTrainingSetup] = useState<TrainingSetup | null>(null);
  const [unaccountedBills, setUnaccountedBills] = useState<UnaccountedBills | null>(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupRefresh, setSetupRefresh] = useState(0);

  const [branding, setBranding] = useState<Branding | null>(null);
  const [brandingBusy, setBrandingBusy] = useState(false);
  const [brandingError, setBrandingError] = useState<string | null>(null);
  const [logoVersion, setLogoVersion] = useState(0);

  const [methods, setMethods] = useState<PaymentMethodSettings | null>(null);
  const [methodsBusy, setMethodsBusy] = useState(false);
  const [methodsError, setMethodsError] = useState<string | null>(null);

  const [payment, setPayment] = useState<PaymentOptions | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentDone, setPaymentDone] = useState<string | null>(null);
  /*
    Which kind of money movement is being recorded.

    Here rather than inside the screen so it survives a trip to another page
    and back — somebody who came to enter a bank charge, went to look at the
    chart of accounts and returned should not find themselves on the invoice
    tab again.
  */
  const [moneyTab, setMoneyTab] = useState<MoneyTab>('invoice');
  /*
    The invoice or bill just filed, waiting to be told whether it was paid.

    Held here rather than in the screen because the two screens are
    different ones: it is set as a form is filed and read on the money
    screen the person then lands on.
  */
  const [justFiled, setJustFiled] = useState<
    { id: string; number: string; kind: 'invoice' | 'bill' } | null
  >(null);

  /*
    Reports.

    Their own period, not the one the sales-and-purchases screen is on.
    Sharing it would mean looking at last month's profit and loss silently
    changes what the summary screen says next time it is opened, which is a
    surprise nobody asked for.
  */
  const [reportRange, setReportRange] = useState<DateRange>(() => presets()[0]!.range);
  /*
    How many periods to compare on the profit and loss.

    Zero is the chosen range as one column, which is where it starts: a
    comparison nobody asked for is three extra Xero columns to read past.
  */
  const [reportPeriods, setReportPeriods] = useState(0);
  /** Narrows the aged summaries to one contact. */
  const [reportFilter, setReportFilter] = useState('');
  /*
    One slot for whichever Xero report is on screen.

    Profit and loss, balance sheet and the aged detail all arrive in the
    same flattened shape, and only one is ever shown, so a state per report
    would be three slots holding two stale answers.
  */
  const [xeroReport, setXeroReport] = useState<XeroReportResponse | null>(null);
  const [aged, setAged] = useState<AgedResponse | null>(null);
  /** The name on a detail report, carried so the heading can say whose it is. */
  const [detailOf, setDetailOf] = useState<string | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  /* Training: the catalogue, the runs and who is on them. */
  const [training, setTraining] = useState<TrainingOverview | null>(null);
  const [delivery, setDelivery] = useState<DeliveryDetail | null>(null);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [trainingBusy, setTrainingBusy] = useState(false);
  const [trainingError, setTrainingError] = useState<string | null>(null);
  /** Bumped after a save, so the list re-reads instead of going stale. */
  const [trainingRefresh, setTrainingRefresh] = useState(0);

  const [people, setPeople] = useState<PeopleResponse | null>(null);
  const [peopleBusy, setPeopleBusy] = useState(false);
  const [peopleError, setPeopleError] = useState<string | null>(null);

  const [gate, setGate] = useState<AccessVariant | 'app' | null>(null);
  const [accessLang, setAccessLang] = useState<Lang>(readLang);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const path = window.location.pathname;

      // A completed sign-out and an abandoned sign-in each land on their own
      // path, because each has something to say rather than an invitation to
      // start again.
      if (path === SIGNED_OUT_PATH) return setGate('signed-out');
      if (path === SIGN_IN_FAILED_PATH) return setGate('failed');
      if (import.meta.env.DEV && path === '/welcome') return setGate('welcome');
      if (import.meta.env.DEV && path === '/unavailable') return setGate('unavailable');
      // The opening wait is over in milliseconds on invented data, so there
      // would otherwise be no way to look at it. Returning without setting a
      // gate or a session leaves the screen exactly where a real slow start
      // leaves it, timers and all.
      if (import.meta.env.DEV && path === '/waiting') return;

      // Local development has no Entra External ID to ask and no /.auth/me to
      // ask it with, so the check is skipped there and the screens run on
      // invented data as before.
      let signedIn = import.meta.env.DEV;
      if (!import.meta.env.DEV) {
        const principal = await loadPrincipal();
        if (cancelled) return;
        if (!principal) return setGate(signInWasAttempted() ? 'failed' : 'welcome');
        clearSignInAttempt();
        signedIn = true;
      }

      let loaded;
      try {
        loaded = await loadSession();
      } catch (error) {
        if (cancelled) return;
        // A refusal means two different things, and telling them apart matters
        // more than it looks. If the browser has no session, the person really
        // has been signed out. If it HAS one and the API still refuses, the
        // fault is on the server — and calling that "signed out" traps them in
        // a loop: they sign in, they are already signed in, they come back to
        // the same refusal. That happened, to a real person, on the first
        // account ever registered.
        if (error instanceof NotSignedInError) {
          return setGate(signedIn ? 'unavailable' : 'signed-out');
        }
        // Anything else means the API did not answer: not deployed, restarting,
        // or the database is unreachable. Rethrowing would leave a signed-in
        // person watching a loading dot with no explanation, which is a worse
        // failure than the one that caused it.
        console.error('Could not load the session', error);
        return setGate('unavailable');
      }
      if (cancelled) return;

      setGate('app');
      setSession(loaded);
      if (loaded.activeOrgId) {
        dispatch({
          type: 'setOrg',
          orgId: loaded.activeOrgId,
          amountsAre: loaded.organisations.find((o) => o.orgId === loaded.activeOrgId)
            ?.defaultAmountsAre,
        });
      } else if (loaded.organisations.length > 0) {
        // No company remembered. That is the ordinary first visit from a new
        // address or a new browser, not a fault, so the picker is opened
        // rather than leaving someone to find it.
        dispatch({ type: 'toggleOrgMenu' });
      }

      /*
        Only the connections list, which spans every company this person can
        reach and so belongs to the session. Everything else on these screens
        belongs to one company and is loaded separately, keyed on which one —
        see below.
      */
      const conns = await loadConnections(loaded.source);
      if (cancelled) return;
      setConnections(conns);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // The choice survives the trip to the Microsoft sign-in page and back.
  useEffect(() => writeLang(state.lang), [state.lang]);

  const organisations = session?.organisations ?? [];

  /*
    The company currently open, and nothing else.

    No falling back to whichever sorts first. The server deliberately refuses
    to guess for anyone holding more than one company — "staff must pick
    explicitly rather than silently acting on whichever sorts first" — and a
    screen that names a company nobody chose, while the server declines to
    serve its data, is worse than a screen that asks.
  */
  const activeOrganisation = organisations.find((o) => o.orgId === state.orgId);

  /**
   * Which optional sections the company now open was given.
   *
   * Absent reads as off. A session served before these existed, or by an
   * older deployment, should withhold the section rather than offer one
   * whose routes would answer "not found" — and the routes refuse it
   * independently either way.
   */
  const enabledFeatures = {
    training: activeOrganisation?.trainingEnabled === true,
  };

  /** Somebody holds companies but has not said which one they mean. */
  const needsOrgChoice = organisations.length > 0 && !activeOrganisation;

  /**
   * The role comes from the database, for the company currently open — not from
   * a control in the browser. Someone can be an administrator in one company
   * and nothing of the sort in the next, and switching company must change what
   * they see.
   *
   * Before a company is chosen there is no such role, and the strongest one
   * held anywhere stands in. That decides one thing only: whether the company
   * picker and the practice's own chrome are drawn at all. It grants nothing —
   * every request names a company and is checked against it again by the
   * server.
   *
   * Defaulting to the weakest role instead, which is what used to happen, hid
   * the picker from exactly the people who needed it: the screen showed a
   * company they had not chosen, with no way to choose another.
   */
  const heldRole = organisations.length
    ? organisations.map((o) => o.role).reduce((a, b) => strongest(a, b))
    : undefined;
  const viewRole = viewRoleFor(activeOrganisation?.role ?? heldRole, state.previewAsClient);

  const screen = effectiveScreen(state, viewRole);
  const listKey = listKeyOf(screen);
  const formKind = formKindOf(screen);
  const reportKind = reportKindOf(screen);
  const trainingView = trainingViewOf(screen);
  const source = session?.source ?? 'local-fixtures';

  /*
    Everything on these screens belongs to one company: its contacts, its
    chart of accounts, its tax rates, its products, its queue and its log.

    This used to run once, when the session loaded, and never again. Switching
    company left all of it behind — so the contact picker offered the previous
    company's customers, and a bill filed against one of them would have
    carried a ContactID belonging to somebody else's Xero organisation.

    Cleared before the fetch, not after it. A slow network would otherwise
    leave one company's customers on screen under another company's name,
    which is the version of this bug that nobody notices.
  */
  useEffect(() => {
    if (!session || !activeOrganisation) return;
    let cancelled = false;

    setLists({});
    setReference(EMPTY_REFERENCE);
    setKnownReferences([]);
    setReferenceLoadedAt(null);
    setDashboardCounts(null);
    dispatch({ type: 'hydrate', queue: [], log: [] });

    void (async () => {
      try {
        const [ref, queue, log, counts] = await Promise.all([
          loadReference(source),
          loadQueue(source),
          loadLog(source),
          loadDashboard(source),
        ]);
        if (cancelled) return;
        setReference(ref.reference);
        setKnownReferences(ref.knownReferences);
        setReferenceLoadedAt(Date.now());
        setDashboardCounts(counts);
        dispatch({ type: 'hydrate', queue, log });
      } catch {
        // Left empty rather than stale. An empty contact picker is a visible
        // problem; the previous company's contacts are an invisible one.
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, state.orgId, source]);

  /*
    Read again every time the screen is opened.

    These lists used to be fetched once and kept for the rest of the session.
    So an invoice raised in Xero after somebody had looked at this screen
    could not appear, however many times they came back to it — the two
    screens simply disagreed, and the portal was the one that was wrong.

    The old rows stay up while the new ones are on their way, because
    replacing a list with an empty one for a second reads as "there is
    nothing here" rather than "hold on".

    One Xero call per visit, against a limit of sixty a minute. A list that
    disagrees with the ledger costs more than the call does.
  */
  const [listRefresh, setListRefresh] = useState(0);
  const [listLoading, setListLoading] = useState(false);

  useEffect(() => {
    if (!session) return;
    const needed = listKey ?? (screen === 'dash' ? 'invoices' : null);
    if (!needed) return;

    let cancelled = false;
    setListLoading(true);
    loadList(needed, source)
      .then((list) => {
        if (!cancelled) setLists((prev) => ({ ...prev, [needed]: list }));
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [listKey, screen, session, source, listRefresh]);

  // Re-read whenever the screen is opened or the company changes. Access is
  // the kind of thing two people edit at once, and a stale list here is a list
  // of who someone *used* to be able to see a client's books.
  useEffect(() => {
    if (screen !== 'people' || !session) return;
    let cancelled = false;
    setPeopleError(null);
    loadPeople(source)
      .then((loaded) => !cancelled && setPeople(loaded))
      .catch(() => !cancelled && setPeople(null));
    return () => {
      cancelled = true;
    };
  }, [screen, state.orgId, session, source]);

  useEffect(() => {
    if (screen !== 'summary' || !session) return;
    let cancelled = false;
    setSummaryLoading(true);
    setSummaryError(null);
    loadSummary(range.from, range.to)
      .then((loaded) => !cancelled && setSummary(loaded))
      .catch(() => {
        if (cancelled) return;
        setSummary(null);
        setSummaryError('Xero could not be reached for this period. Try a shorter one.');
      })
      .finally(() => !cancelled && setSummaryLoading(false));
    return () => {
      cancelled = true;
    };
  }, [screen, range, state.orgId, session]);

  /*
    Whichever report is open, and only that one.

    Each one costs Xero calls — the aged summaries read every outstanding
    invoice — and loading them all when one is on screen would spend a
    client's rate limit on answers nobody asked for.
  */
  useEffect(() => {
    const kind = reportKindOf(screen);
    if (kind === null || !session) return;

    let cancelled = false;
    setReportLoading(true);
    setReportError(null);

    const detail = kind === 'receivables-detail' || kind === 'payables-detail';
    const contactId = reportContactOf(screen);

    const wanted =
      kind === 'pl'
        ? loadProfitAndLoss(reportRange.from, reportRange.to, reportPeriods, source).then((loaded) => {
            if (!cancelled) setXeroReport(loaded);
          })
        : kind === 'balance'
          ? // The balance sheet is struck on a day, and the day it uses is the
            // end of the range the rest of the reports share. One date picker
            // in the reader's head rather than two.
            loadBalanceSheet(reportRange.to, reportPeriods, source).then((loaded) => {
              if (!cancelled) setXeroReport(loaded);
            })
          : detail && contactId
            ? loadAgedDetail(
                kind === 'receivables-detail' ? 'receivable' : 'payable',
                contactId,
                reportRange.to,
                source,
              ).then((loaded) => {
                if (!cancelled) setXeroReport(loaded);
              })
            : loadAged(kind === 'receivables' ? 'receivable' : 'payable', source).then((loaded) => {
                if (!cancelled) setAged(loaded);
              });

    wanted
      .catch(() => {
        if (cancelled) return;
        // Cleared rather than left behind: a stale report under a new error
        // reads as the figures still being good.
        if (kind === 'receivables' || kind === 'payables') setAged(null);
        else setXeroReport(null);
        setReportError('Xero could not be reached for this report. Try a shorter period.');
      })
      .finally(() => {
        if (!cancelled) setReportLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [screen, reportRange, reportPeriods, state.orgId, session, source]);

  /*
    The catalogue and the runs, for whichever training screen is open.

    Both lists come in one call: the deliveries screen needs the catalogue
    for its dropdown and the courses screen wants to say how many runs each
    course has had, so fetching them apart would mean two chances to draw
    half a screen.
  */
  useEffect(() => {
    /*
      Also loaded for the bill form and the money screens, which offer a
      course to put a cost on. It is a database read with no Xero call in
      it, so this is cheap — and a company with no courses simply gets an
      empty list and no dropdown.
    */
    const wantsCourses =
      trainingViewOf(screen) !== null || screen === 'form:bill' || screen === 'payment';
    if (!wantsCourses || !session) return;

    let cancelled = false;
    setTrainingLoading(true);
    setTrainingError(null);
    loadTraining(source)
      .then((loaded) => !cancelled && setTraining(loaded))
      .catch(() => !cancelled && setTrainingError('That could not be loaded. Please try again.'))
      .finally(() => !cancelled && setTrainingLoading(false));

    return () => {
      cancelled = true;
    };
  }, [screen, state.orgId, session, source, trainingRefresh]);

  /** One delivery and its bookings, only while that screen is open. */
  useEffect(() => {
    const view = trainingViewOf(screen);
    if (view?.view !== 'delivery' || !session) return;

    let cancelled = false;
    setTrainingLoading(true);
    setTrainingError(null);
    loadDelivery(view.deliveryId, source)
      .then((loaded) => !cancelled && setDelivery(loaded))
      .catch(() => {
        if (cancelled) return;
        // Cleared, not left behind: another cohort's numbers under a fresh
        // error would read as this one's.
        setDelivery(null);
        setTrainingError('That delivery could not be opened.');
      })
      .finally(() => !cancelled && setTrainingLoading(false));

    return () => {
      cancelled = true;
    };
  }, [screen, state.orgId, session, source, trainingRefresh]);

  const onSaveTraining = useCallback(
    async (what: 'course' | 'delivery' | 'registration', input: unknown) => {
      setTrainingBusy(true);
      setTrainingError(null);
      try {
        if (what === 'course') await saveCourse(input as CourseInput, source);
        else if (what === 'delivery') await saveDelivery(input as DeliveryInput, source);
        else await saveRegistration(input as RegistrationInput, source);
        setTrainingRefresh((n) => n + 1);
      } catch (error) {
        setTrainingError(error instanceof Error ? error.message : String(error));
      } finally {
        setTrainingBusy(false);
      }
    },
    [source],
  );

  useEffect(() => {
    if (screen !== 'payment' || !session) return;
    let cancelled = false;
    setPaymentLoading(true);
    setPaymentError(null);
    loadPaymentOptions(source)
      .then((loaded) => !cancelled && setPayment(loaded))
      .catch(() => !cancelled && setPaymentError('Xero could not be reached.'))
      .finally(() => !cancelled && setPaymentLoading(false));
    return () => {
      cancelled = true;
    };
  }, [screen, state.orgId, session]);

  /**
   * The training setup screen: which accounts count, and what fell out.
   *
   * Both together, because the flag is only as good as the account list
   * and showing an empty flag beside an empty list would read as good
   * news. Re-run when the accounts are saved, since the list below them
   * is computed from exactly that.
   */
  useEffect(() => {
    if (screen !== 'training:setup' || !session) return;
    let cancelled = false;
    setSetupLoading(true);

    void Promise.all([
      loadTrainingSetup(source).catch(() => null),
      loadUnaccountedBills(source).catch(() => null),
    ])
      .then(([setup, bills]) => {
        if (cancelled) return;
        setTrainingSetup(setup);
        setUnaccountedBills(bills);
      })
      .finally(() => !cancelled && setSetupLoading(false));

    return () => {
      cancelled = true;
    };
  }, [screen, session, source, setupRefresh]);

  useEffect(() => {
    if (screen !== 'xero' || !session) return;
    let cancelled = false;
    setConnectionLoading(true);
    loadConnectionDetail()
      .then((loaded) => !cancelled && setConnectionDetail(loaded))
      .catch(() => !cancelled && setConnectionDetail(null))
      .finally(() => !cancelled && setConnectionLoading(false));
    return () => {
      cancelled = true;
    };
  }, [screen, state.orgId, session]);

  useEffect(() => {
    if (screen !== 'branding' || !session) return;
    let cancelled = false;
    setBrandingError(null);
    loadBranding(source)
      .then((loaded) => !cancelled && setBranding(loaded))
      .catch(() => !cancelled && setBranding(null));
    return () => {
      cancelled = true;
    };
  }, [screen, state.orgId, session]);

  const onUploadLogo = useCallback(async (file: File) => {
    setBrandingBusy(true);
    setBrandingError(null);
    try {
      await uploadLogo(file);
      setBranding(await loadBranding(source));
      // The browser caches an image by its address, and the address has not
      // changed. Bumping this changes it.
      setLogoVersion((v) => v + 1);
    } catch (error) {
      setBrandingError(error instanceof Error ? error.message : String(error));
    } finally {
      setBrandingBusy(false);
    }
  }, []);

  useEffect(() => {
    if (screen !== 'methods' || !session) return;
    let cancelled = false;
    setMethodsError(null);
    loadPaymentMethods(source)
      .then((loaded) => !cancelled && setMethods(loaded))
      .catch(() => !cancelled && setMethods(null));
    return () => {
      cancelled = true;
    };
  }, [screen, state.orgId, session, source]);

  const onSaveMethods = useCallback(
    async (chosen: string[], defaultMethod: string | null) => {
      setMethodsBusy(true);
      setMethodsError(null);
      try {
        await savePaymentMethods(chosen, defaultMethod, source);
        setMethods(await loadPaymentMethods(source));
        // The payment form offers what this screen decides, so what it is
        // holding is now out of date.
        setPayment(null);
      } catch (error) {
        setMethodsError(error instanceof Error ? error.message : String(error));
        throw error;
      } finally {
        setMethodsBusy(false);
      }
    },
    [source],
  );

  const onSaveBranding = useCallback(
    async (footer: BrandingFooter, presentation: Presentation) => {
      setBrandingBusy(true);
      setBrandingError(null);
      try {
        await saveBranding(footer, presentation, source);
        setBranding(await loadBranding(source));
      } catch (error) {
        setBrandingError(error instanceof Error ? error.message : String(error));
        // Rethrown so the screen knows not to claim it saved.
        throw error;
      } finally {
        setBrandingBusy(false);
      }
    },
    [source],
  );

  const onRemoveLogo = useCallback(async () => {
    setBrandingBusy(true);
    setBrandingError(null);
    try {
      await removeLogo();
      setBranding(await loadBranding(source));
      setLogoVersion((v) => v + 1);
    } catch (error) {
      setBrandingError(error instanceof Error ? error.message : String(error));
    } finally {
      setBrandingBusy(false);
    }
  }, []);

  const onRecordPayment = useCallback(
    async (input: {
      invoiceId: string;
      accountCode: string;
      method: string;
      date: string;
      amount: string;
      reference: string;
    }) => {
      setPaymentBusy(true);
      setPaymentError(null);
      setPaymentDone(null);
      try {
        await recordPayment(input);
        setPaymentDone('Recorded in Xero.');
        // The question that brought them here has been answered, so the
        // screen stops being about the document they just filed.
        setJustFiled(null);
        // The invoice just paid is no longer outstanding, so the list it came
        // from is already wrong. Re-read rather than leave it offering a
        // payment that would now be a second one.
        const refreshed = await loadPaymentOptions(source);
        setPayment(refreshed);
      } catch (error) {
        setPaymentError(error instanceof Error ? error.message : String(error));
      } finally {
        setPaymentBusy(false);
      }
    },
    [],
  );

  /**
   * Spending, receiving, transferring — everything that is not a payment
   * against an invoice.
   *
   * Shares the payment screen's busy and answer state deliberately: it is one
   * page with four tabs, and two sets of "Recorded in Xero" could both be on
   * screen at once, each about a different entry.
   */
  const onRecordMoney = useCallback(
    async (entry: MoneyEntry) => {
      setPaymentBusy(true);
      setPaymentError(null);
      setPaymentDone(null);
      try {
        const result = await recordMoney(entry, source);
        setPaymentDone(result.message ?? 'Recorded in Xero.');
      } catch (error) {
        setPaymentError(error instanceof Error ? error.message : String(error));
      } finally {
        setPaymentBusy(false);
      }
    },
    [source],
  );

  /**
   * Changing tab clears the last answer.
   *
   * Without this, "Recorded in Xero" from the bank charge just entered is
   * still sitting above the transfer form when you arrive at it, where it
   * reads as though the transfer went through. The entry it was about is no
   * longer on screen, so the sentence has nothing left to be true of.
   */
  const onMoneyTab = useCallback((next: MoneyTab) => {
    setMoneyTab(next);
    setPaymentDone(null);
    setPaymentError(null);
  }, []);

  /**
   * Bill a cohort, then re-read it.
   *
   * The re-read is the point: it brings back each new invoice's number and
   * state from Xero, so the screen shows what was actually filed rather
   * than what we asked for.
   */
  /**
   * Turn one of the two client switches on or off.
   *
   * The session's copy of the company is updated as well as the
   * connection detail, because the sidebar reads Training from there:
   * leaving it stale would keep offering a section whose routes have just
   * started refusing, or hide one that now works. Patched in place rather
   * than refetched — what changed is known exactly, and reloading the
   * session would re-run the sign-in gate and the company picker.
   */
  const onSetFeatures = useCallback(
    async (change: { training?: boolean; xeroProjects?: boolean }) => {
      setFeaturesBusy(true);
      setFeaturesError(null);
      try {
        const saved = await setOrganisationFeatures(change, source);

        setConnectionDetail((current) =>
          current === null
            ? current
            : {
                ...current,
                features: saved.features,
                missing: saved.missing,
                needsReconnect: saved.needsReconnect,
              },
        );

        setSession((current) =>
          current === null
            ? current
            : {
                ...current,
                organisations: current.organisations.map((organisation) =>
                  organisation.orgId === state.orgId
                    ? {
                        ...organisation,
                        trainingEnabled: saved.features.training,
                        xeroProjectsEnabled: saved.features.xeroProjects,
                      }
                    : organisation,
                ),
              },
        );
      } catch (error) {
        setFeaturesError(error instanceof Error ? error.message : String(error));
      } finally {
        setFeaturesBusy(false);
      }
    },
    [source, state.orgId],
  );

  const onInvoiceCohort = useCallback(
    async (
      deliveryId: number,
      registrationIds: number[] | null,
      date: string | null,
      /** Null sends no due date, so the company's own Xero terms apply. */
      dueDate: string | null,
    ) => {
      setTrainingBusy(true);
      setTrainingError(null);
      try {
        const result = await invoiceCohort(deliveryId, registrationIds, date, dueDate, source);
        if (result.failed.length > 0) {
          /*
            Some invoices exist and some do not. Saying only "done" would
            hide the ones that did not, and the names are what makes it
            actionable.

            Xero's own sentence where it gave one — a wrong account code, a
            locked period, an archived contact. Every one of these used to
            arrive as "xero_refused", which named a problem nobody could
            act on. The same complaint on twenty students is said once.
          */
          const reasons = [
            ...new Set(result.failed.map((f) => f.detail).filter((d): d is string => !!d)),
          ];
          const names = result.failed.map((f) => f.name).filter(Boolean).join(', ');

          setTrainingError(
            [
              `Xero would not take ${result.failed.length} of these: ${names}`,
              ...reasons,
            ].join(' — '),
          );
        }
        setTrainingRefresh((n) => n + 1);
      } catch (error) {
        setTrainingError(error instanceof Error ? error.message : String(error));
      } finally {
        setTrainingBusy(false);
      }
    },
    [source],
  );

  /**
   * Run a training write, then re-read the delivery.
   *
   * The re-read is not a nicety: linking a cost changes what the course
   * made, and that figure is computed on the server from Xero's own
   * documents. Working it out again in the browser would be a second
   * answer to a question that must only have one.
   */
  const onTrainingAction = useCallback(async (action: () => Promise<void>) => {
    setTrainingBusy(true);
    setTrainingError(null);
    try {
      await action();
      setTrainingRefresh((n) => n + 1);
    } catch (error) {
      setTrainingError(error instanceof Error ? error.message : String(error));
    } finally {
      setTrainingBusy(false);
    }
  }, []);

  const refreshPeople = useCallback(() => {
    loadPeople(source)
      .then(setPeople)
      .catch(() => undefined);
  }, [source]);

  /*
    Returns whether it worked, which the screen needs for more than a tidy
    form. A password chosen here is shown once afterwards, and showing it
    after a failed invitation would have somebody read out a password for an
    account that was never created.
  */
  const onInvite = useCallback(
    async (
      email: string,
      role: InvitableRole,
      name: string | null,
      password: string | null,
    ): Promise<boolean> => {
      setPeopleBusy(true);
      setPeopleError(null);
      try {
        const result = await invitePerson(email, role, name, password, source);
        refreshPeople();
        if (password !== null && !result.passwordApplied) {
          // The grant was made; the password was not, because the directory
          // already had that address. Said plainly, because the difference
          // decides what the admin tells the client next.
          setPeopleError(
            'Access was given, but the password was not set: that address already had a sign-in. They keep the password they already have, or can reset it from the sign-in page.',
          );
          return false;
        }
        return true;
      } catch (error) {
        setPeopleError(error instanceof Error ? error.message : String(error));
        return false;
      } finally {
        setPeopleBusy(false);
      }
    },
    [refreshPeople, source],
  );

  const onSetPassword = useCallback(
    async (
      target: { userId?: number; invitationId?: number },
      password: string,
    ): Promise<boolean> => {
      setPeopleBusy(true);
      setPeopleError(null);
      try {
        await setPersonPassword(target, password, source);
        refreshPeople();
        return true;
      } catch (error) {
        setPeopleError(error instanceof Error ? error.message : String(error));
        return false;
      } finally {
        setPeopleBusy(false);
      }
    },
    [refreshPeople, source],
  );

  const onCreateSignIn = useCallback(
    async (invitationId: number) => {
      setPeopleBusy(true);
      setPeopleError(null);
      try {
        await createSignIn(invitationId);
        refreshPeople();
      } catch (error) {
        setPeopleError(error instanceof Error ? error.message : String(error));
      } finally {
        setPeopleBusy(false);
      }
    },
    [refreshPeople],
  );

  const onRevoke = useCallback(
    async (target: { userId?: number; invitationId?: number }) => {
      setPeopleBusy(true);
      setPeopleError(null);
      try {
        await revokePerson(target);
        refreshPeople();
      } catch (error) {
        setPeopleError(error instanceof Error ? error.message : String(error));
      } finally {
        setPeopleBusy(false);
      }
    },
    [refreshPeople],
  );

  const t = useMemo(() => createTranslate(state.lang), [state.lang]);
  const renderMessage = useMemo(() => createMessageRenderer(state.lang), [state.lang]);
  const rtl = isRtl(state.lang);

  const active = activeOrganisation;
  const orgName = active ? t(active.name) : '';

  const [switchingOrg, setSwitchingOrg] = useState(false);

  /*
    Which company a request belongs to is held in a cookie the server sets. This
    used to change the screen first and set the cookie afterwards, without
    waiting:

        dispatch({ type: 'setOrg', orgId });
        void setActiveOrganisation(orgId, source);

    Between those two lines the screen said one company and the server still
    believed another, and everything written in that window went to the wrong
    one. Normally that gap is a few hundred milliseconds. The database pauses
    itself when idle and takes the better part of a minute to wake, so the gap
    is worst exactly when somebody has just sat down to work — and a logo
    uploaded then is filed under whichever company they were looking at before.

    So the server agrees first, and the screen follows. Until it answers, the
    old company stays on screen, because that is what is true.
  */
  const onSelectOrganisation = useCallback(
    async (orgId: number) => {
      if (orgId === state.orgId) return;
      setSwitchingOrg(true);
      try {
        await setActiveOrganisation(orgId, source);
        dispatch({
          type: 'setOrg',
          orgId,
          // This company's own tax basis, applied to the forms as we arrive.
          amountsAre: session?.organisations.find((o) => o.orgId === orgId)?.defaultAmountsAre,
        });
      } catch {
        // The screen stays where it was, which is where the server still is.
        setBrandingError('That company could not be opened. Please try again.');
      } finally {
        setSwitchingOrg(false);
      }
    },
    [source, state.orgId, session],
  );

  /*
    Leaving the money screen ends the question about the document just
    filed. Without this it would still be waiting there tomorrow, and the
    next person to open Payments would find an invoice chosen for them
    that they had not chosen.
  */
  useEffect(() => {
    if (screen !== 'payment' && justFiled !== null) setJustFiled(null);
  }, [screen, justFiled]);

  /** Read one record's status back from Xero. Never a background sync. */
  const onReadRow = useCallback((key: string, current: string) => {
    dispatch({ type: 'setRead', key, status: 'CHECKING' });
    const next = current === 'DRAFT' ? 'IN XERO' : current === 'FAILED' ? 'FAILED' : 'AUTHORISED';
    window.setTimeout(() => dispatch({ type: 'setRead', key, status: next }), 700);
  }, []);

  const onPush = useCallback(async () => {
    if (!formKind || !active) return;
    const doc = state.docs[formKind];
    const amountsAre = (
      typeof doc.fields.amountsAre === 'string' ? doc.fields.amountsAre : 'Exclusive'
    ) as LineAmountType;

    try {
      // No Xero tenant id here — the server resolves it from the signed-in
      // session after checking this user may reach this company.
      const result = await pushDocument(
        {
          kind: formKind,
          orgId: active.orgId,
          fields: doc.fields,
          lines: doc.lines,
          amountsAre,
          /*
            Put this bill on a course as it is filed. Written only after
            Xero accepts the bill, and a failure to link never fails the
            push — see api/src/functions/documents.ts.
          */
          deliveryId:
            formKind === 'bill' && typeof doc.fields.trainingDeliveryId === 'string'
              ? Number(doc.fields.trainingDeliveryId) || null
              : null,
        },
        source,
      );
      dispatch({ type: 'markSaved' });

      /*
        A filed invoice or bill goes straight to the question that follows
        it: was it paid.

        Asked here because this is the only moment anybody knows the
        answer without looking it up. Left to later it means finding the
        document again in a list of everything outstanding, which is the
        step that gets put off and then forgotten — and an invoice that
        was paid in cash on the day shows as overdue a month later.

        Only where there is something to pay. createdInvoice returns null
        for a zero-total document, which is complete the moment it exists.
        Nothing is pushed to Xero by arriving here: the document is filed
        and owing either way, and "Leave it unpaid" is a real answer.
      */
      if (formKind === 'invoice' || formKind === 'bill') {
        const filed = createdInvoice(result.data);
        if (filed) {
          setJustFiled({ ...filed, kind: formKind });
          setMoneyTab('invoice');
          dispatch({ type: 'go', screen: 'payment' });
        }
      }

      // Someone who came here from an invoice to create a missing contact goes
      // straight back to it, with the contact chosen. The reference data is
      // reloaded first, or the dropdown they return to would still not contain
      // the thing they just made.
      const back = state.returnTo;
      if (formKind === 'item' && back) {
        const newCode = createdItemCode(result.data);
        const refreshed = await loadReference(source);
        setReference(refreshed.reference);
        setKnownReferences(refreshed.knownReferences);

        // The line it was started from is remembered, so it comes back chosen
        // rather than merely existing somewhere in a list.
        const line = back.field.startsWith('line:') ? Number(back.field.slice(5)) : NaN;
        if (newCode && Number.isInteger(line)) {
          dispatch({ type: 'setLine', kind: back.kind, index: line, key: 'itemCode', value: newCode });
        }
        dispatch({ type: 'clearReturn' });
        dispatch({ type: 'go', screen: back.screen });
      }

      if (formKind === 'contact' && back) {
        const newId = createdContactId(result.data);
        const refreshed = await loadReference(source);
        setReference(refreshed.reference);
        setKnownReferences(refreshed.knownReferences);
        if (newId) {
          dispatch({ type: 'setField', kind: back.kind, key: back.field, value: newId });
        }
        dispatch({ type: 'clearReturn' });
        dispatch({ type: 'go', screen: back.screen });
      }
    } catch (error) {
      // This used to be swallowed, on the theory that the pre-push checks had
      // already explained any problem. They had not: these are Xero's own
      // objections to the finished document, which our checks cannot know in
      // advance — an item code that does not exist in that organisation, for
      // instance. Pressing the button and having nothing at all happen is the
      // worst possible answer, and it is what happened.
      dispatch({
        type: 'pushFailed',
        messages:
          error instanceof XeroRejectedError
            ? error.messages
            : [error instanceof Error ? error.message : String(error)],
      });
    }
  }, [formKind, active, state.docs, state.returnTo, source]);

  if (gate !== null && gate !== 'app') {
    return (
      <AccessScreen
        variant={gate}
        lang={accessLang}
        onLang={(next) => {
          setAccessLang(next);
          writeLang(next);
        }}
        onSignIn={() => beginSignIn()}
      />
    );
  }

  /*
    Covers both halves of the opening wait: asking Microsoft who this is, and
    then fetching everything they can reach. They are one wait from the
    reader's side, and the language is the one chosen on this device, because
    there is no session yet to hold a preference.
  */
  if (!session) {
    return <WaitingScreen lang={accessLang} />;
  }

  if (session.organisations.length === 0) {
    return <NoAccessScreen lang={state.lang} email={session.user.email} />;
  }

  // A document laid out for paper stands alone: no sidebar, no header, nothing
  // that would otherwise be printed alongside it.
  if (screen.startsWith('print:')) {
    const [, printKind, printId] = screen.split(':');
    const backTo =
      printKind === 'invoice' ? 'invoices'
      : printKind === 'bill' ? 'bills'
      : printKind === 'quote' ? 'quotes'
      : 'pos';
    return (
      <div
        dir={rtl ? 'rtl' : 'ltr'}
        style={{ fontFamily: theme.fontBody, color: theme.text, fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}
      >
        <PrintDocument
          kind={printKind ?? ''}
          id={printId ?? ''}
          lang={state.lang}
          source={source}
          t={t}
          onClose={() => dispatch({ type: 'go', screen: `list:${backTo}` })}
        />
      </div>
    );
  }

  return (
    <div
      dir={rtl ? 'rtl' : 'ltr'}
      style={{
        display: 'flex',
        height: '100vh',
        minHeight: '640px',
        overflow: 'hidden',
        fontFamily: theme.fontBody,
        color: theme.text,
        background: theme.canvas,
        fontSize: '13px',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <Sidebar
        switching={switchingOrg}
        state={state}
        screen={screen}
        organisations={organisations}
        active={active}
        role={viewRole}
        features={enabledFeatures}
        user={session.user}
        rtl={rtl}
        t={t}
        dispatch={dispatch}
        onSelectOrganisation={onSelectOrganisation}
      />

      <main
        style={{
          flex: 1,
          minWidth: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Header
          crumbRoot={crumbRootFor(screen, orgName, lists, t)}
          crumbLeaf={crumbLeafFor(screen, lists, t)}
          role={viewRole}
          canPreview={isFirmRole(viewRoleFor(activeOrganisation?.role, false))}
          /*
            The open company's own connection, matched on our own org id —
            the browser is never told the real Xero tenant id, deliberately,
            so this list carries ours in its place. Null until it is known,
            so the badge stays away rather than guessing green.
          */
          connection={
            activeOrganisation
              ? (connections.find((c) => c.id === String(activeOrganisation.orgId)) ?? null)
              : null
          }
          previewing={state.previewAsClient}
          lang={state.lang}
          t={t}
          dispatch={dispatch}
        />

        {source === 'local-fixtures' && <DemoDataBanner lang={state.lang} />}

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/*
            Nothing is open, so nothing is shown. The companies are listed here
            rather than only in the sidebar, because somebody arriving for the
            first time should not have to discover a picker to get started.
          */}
          {needsOrgChoice ? (
            <div style={{ padding: '30px 24px 40px', maxWidth: '560px' }}>
              <h1 style={{ fontSize: '19px', fontWeight: 700, margin: '0 0 7px' }}>
                {t('Choose a company')}
              </h1>
              <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 18px', lineHeight: 1.65 }}>
                {t('You can reach more than one. Nothing is open until you choose.')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {organisations.map((option) => (
                  <button
                    key={option.orgId}
                    type="button"
                    disabled={switchingOrg}
                    onClick={() => void onSelectOrganisation(option.orgId)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      width: '100%',
                      background: '#fff',
                      border: `1px solid ${theme.borderStrong}`,
                      borderRadius: theme.radiusCard,
                      padding: '13px 15px',
                      fontSize: '13.5px',
                      fontWeight: 600,
                      color: theme.text,
                      cursor: switchingOrg ? 'default' : 'pointer',
                      textAlign: 'start',
                    }}
                  >
                    <span>{t(option.name)}</span>
                    <span style={{ fontSize: '11.5px', fontWeight: 500, color: theme.textMuted }}>
                      {option.currency}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
          {screen === 'dash' && (
            <Dashboard
              counts={dashboardCounts}
              reference={reference}
              referenceLoadedAt={referenceLoadedAt}
              state={state}
              role={viewRole}
              orgName={orgName}
              invoices={lists.invoices}
              t={t}
              dispatch={dispatch}
            />
          )}

          {listKey &&
            (lists[listKey] ? (
              <ListScreen
                listKey={listKey}
                list={lists[listKey]!}
                onRefresh={() => setListRefresh((n) => n + 1)}
                refreshing={listLoading}
                state={state}
                role={viewRole}
                rtl={rtl}
                t={t}
                dispatch={dispatch}
                onReadRow={onReadRow}
              />
            ) : (
              <div style={{ padding: '40px', color: theme.textMuted }}>…</div>
            ))}

          {formKind && (
            <FormScreen
              kind={formKind}
              state={state}
              role={viewRole}
              rtl={rtl}
              orgName={orgName}
              defaultAmountsAre={active?.defaultAmountsAre}
              reference={reference}
              knownReferences={knownReferences}
              deliveries={training?.deliveries ?? []}
              t={t}
              renderMessage={renderMessage}
              dispatch={dispatch}
              onPush={onPush}
            />
          )}

          {screen === 'queue' && <QueueScreen state={state} t={t} renderMessage={renderMessage} dispatch={dispatch} />}
          {screen === 'log' && <LogScreen state={state} t={t} renderMessage={renderMessage} dispatch={dispatch} />}
          {screen === 'branding' && (
            <BrandingScreen
              branding={branding}
              busy={brandingBusy}
              error={brandingError}
              version={logoVersion}
              t={t}
              onUpload={onUploadLogo}
              onRemove={onRemoveLogo}
              onSave={onSaveBranding}
            />
          )}

          {screen.startsWith('doc:') && (() => {
            const [, docKind, docId] = screen.split(':');
            const backTo =
              docKind === 'invoice' ? 'invoices'
              : docKind === 'bill' ? 'bills'
              : docKind === 'quote' ? 'quotes'
              : 'pos';
            return (
              <DocumentScreen
                kind={docKind ?? ''}
                id={docId ?? ''}
                source={source}
                isAdmin={can(viewRole, 'operate')}
                lang={state.lang}
                t={t}
                // Back to wherever this was opened from — a training cohort
                // keeps its own return path — and otherwise to the list the
                // document belongs to.
                onBack={() =>
                  dispatch({ type: 'go', screen: state.docBackTo ?? `list:${backTo}` })
                }
                onPrint={() =>
                  dispatch({
                    type: 'go',
                    screen: `print:${docKind}:${docId}`,
                    back: state.docBackTo ?? undefined,
                  })
                }
              />
            );
          })()}

          {screen.startsWith('import:') && (() => {
            const backTo = screen.slice('import:'.length) || 'customers';
            return (
              <ImportContactsScreen
                source={source}
                t={t}
                onBack={() => dispatch({ type: 'go', screen: `list:${backTo}` })}
                onDone={() => dispatch({ type: 'go', screen: `list:${backTo}` })}
              />
            );
          })()}

          {screen === 'methods' && (
            <PaymentMethodsScreen
              settings={methods}
              busy={methodsBusy}
              error={methodsError}
              lang={state.lang}
              t={t}
              onSave={onSaveMethods}
            />
          )}

          {trainingView?.view === 'setup' && (
            <TrainingSetupScreen
              setup={trainingSetup}
              unaccounted={unaccountedBills}
              accounts={reference.accounts}
              loading={setupLoading}
              busy={trainingBusy}
              // Deciding which accounts carry a course cost changes what
              // the month-end flag watches. That is an 'operate' call.
              canEdit={can(viewRole, 'operate')}
              error={trainingError}
              t={t}
              onSaveAccounts={(codes) =>
                void onTrainingAction(async () => {
                  await saveDirectCostAccounts(codes, source);
                  setSetupRefresh((n) => n + 1);
                })
              }
              onOpenBill={(xeroId) =>
                dispatch({
                  type: 'go',
                  screen: `doc:bill:${xeroId}`,
                  back: 'training:setup',
                })
              }
            />
          )}

          {trainingView?.view === 'courses' && (
            <CoursesScreen
              courses={training?.courses ?? []}
              currency={training?.currency ?? ''}
              loading={trainingLoading}
              busy={trainingBusy}
              error={trainingError}
              lang={state.lang}
              canSell={can(viewRole, 'operate')}
              t={t}
              onSave={(course) => void onSaveTraining('course', course)}
              onSellAsProduct={(courseId, unlink) =>
                void onTrainingAction(() =>
                  setCourseItem(courseId, unlink ? { unlink: true } : {}, source),
                )
              }
            />
          )}

          {trainingView?.view === 'deliveries' && (
            <DeliveriesScreen
              courses={training?.courses ?? []}
              deliveries={training?.deliveries ?? []}
              loading={trainingLoading}
              busy={trainingBusy}
              error={trainingError}
              t={t}
              onSave={(entry) => void onSaveTraining('delivery', entry)}
              onOpen={(deliveryId) =>
                dispatch({ type: 'go', screen: `training:delivery:${deliveryId}` })
              }
            />
          )}

          {trainingView?.view === 'delivery' && (
            <DeliveryScreen
              detail={delivery}
              contacts={reference.contacts}
              loading={trainingLoading}
              busy={trainingBusy}
              error={trainingError}
              t={t}
              canClose={can(viewRole, 'operate')}
              source={source}
              onSave={(registration) => void onSaveTraining('registration', registration)}
              onInvoice={(registrationIds, date, dueDate) =>
                void onInvoiceCohort(trainingView.deliveryId, registrationIds, date, dueDate)
              }
              onLinkCost={(cost) => void onTrainingAction(() => linkCost(trainingView.deliveryId, cost, source))}
              onUnlinkCost={(costId) =>
                void onTrainingAction(() => unlinkCost(trainingView.deliveryId, costId, source))
              }
              onLinkCredit={(credit) =>
                void onTrainingAction(() => linkCredit(trainingView.deliveryId, credit, source))
              }
              onUnlinkCredit={(creditId) =>
                void onTrainingAction(() => unlinkCredit(trainingView.deliveryId, creditId, source))
              }
              /*
                File a new bill that is already on this course.

                The ordinary bill form, with the course filled in and a
                way back to the cohort. Reset first, so a half-typed bill
                abandoned on another screen does not surface here with
                somebody else's supplier on it.
              */
              onNewBillForDelivery={() => {
                dispatch({ type: 'resetForm', kind: 'bill', amountsAre: active?.defaultAmountsAre });
                dispatch({
                  type: 'setField',
                  kind: 'bill',
                  key: 'trainingDeliveryId',
                  value: String(trainingView.deliveryId),
                });
                dispatch({
                  type: 'go',
                  screen: 'form:bill',
                  back: `training:delivery:${trainingView.deliveryId}`,
                });
              }}
              onSetClosed={(closed) =>
                void onTrainingAction(() => setDeliveryClosed(trainingView.deliveryId, closed, source))
              }
              onOpenInvoice={(invoiceId) =>
                dispatch({
                  type: 'go',
                  screen: `doc:invoice:${invoiceId}`,
                  // Back returns to the cohort it was opened from, not to
                  // the list of every invoice the company has.
                  back: `training:delivery:${trainingView.deliveryId}`,
                })
              }
              onBack={() => dispatch({ type: 'go', screen: 'training:deliveries' })}
            />
          )}

          {reportKind !== null && (
            <ReportsScreen
              kind={reportKind}
              range={reportRange}
              onRange={setReportRange}
              periods={reportPeriods}
              onPeriods={setReportPeriods}
              filter={reportFilter}
              onFilter={setReportFilter}
              xeroReport={xeroReport}
              aged={aged}
              detailOf={detailOf}
              onDrillInto={(contactId, contact) => {
                /*
                  The name is held in state and the id in the screen key.
                  The id is what the next call needs and must survive a
                  reload; the name is only a heading, and a reload that
                  lands on the report with Xero's own title above it has
                  lost nothing worth keeping.
                */
                setDetailOf(contact);
                const base =
                  reportKind === 'receivables' ? 'receivables-detail' : 'payables-detail';
                dispatch({ type: 'go', screen: `report:${base}:${contactId}` });
              }}
              onBack={() => {
                setDetailOf(null);
                dispatch({
                  type: 'go',
                  screen:
                    reportKind === 'receivables-detail'
                      ? 'report:receivables'
                      : 'report:payables',
                });
              }}
              loading={reportLoading}
              error={reportError}
              t={t}
            />
          )}

          {screen === 'payment' && (
            <MoneyScreen
              tab={moneyTab}
              onTab={onMoneyTab}
              justFiled={justFiled}
              onLeaveUnpaid={() => {
                // Nothing to undo: it was filed and owing a second ago and
                // it still is. Back to the list it came from.
                const back = justFiled?.kind === 'invoice' ? 'list:invoices' : 'list:bills';
                setJustFiled(null);
                dispatch({ type: 'go', screen: back });
              }}
              options={payment}
              reference={reference}
              deliveries={training?.deliveries ?? []}
              currency={activeOrganisation?.currency ?? payment?.currency ?? ''}
              loading={paymentLoading}
              busy={paymentBusy}
              error={paymentError}
              done={paymentDone}
              lang={state.lang}
              t={t}
              onRecordPayment={onRecordPayment}
              onRecordMoney={onRecordMoney}
            />
          )}

          {screen === 'summary' && (
            <SummaryScreen
              range={range}
              data={summary}
              loading={summaryLoading}
              error={summaryError}
              t={t}
              onRange={setRange}
            />
          )}

          {screen === 'people' && (
            <PeopleScreen
              organisation={orgName}
              members={people?.members ?? []}
              invited={people?.invited ?? []}
              grantableRoles={people?.grantableRoles ?? ['client_user', 'client_admin']}
              createsSignIn={people?.createsSignIn ?? false}
              canRemoveFirmMembers={people?.canRemoveFirmMembers ?? false}
              busy={peopleBusy}
              error={peopleError}
              t={t}
              onInvite={onInvite}
              onRevoke={onRevoke}
              onCreateSignIn={onCreateSignIn}
              onSetPassword={onSetPassword}
            />
          )}

          {screen === 'conns' && (
            <ConnectionsScreen
              tenants={connections}
              t={t}
              onOpen={(tenant) => {
                const index = connections.findIndex((c) => c.tenantId === tenant.tenantId);
                const match = organisations[index];
                if (match) onSelectOrganisation(match.orgId);
                dispatch({ type: 'go', screen: 'xero' });
              }}
            />
          )}
          {screen === 'xero' && (
            <XeroScreen
              detail={connectionDetail}
              loading={connectionLoading}
              t={t}
              // What a client is given is the practice's decision, not a
              // role inside the client's own company — a client_admin
              // reaches 'operate' there quite legitimately.
              canManageFeatures={isFirmRole(viewRole)}
              featuresBusy={featuresBusy}
              featuresError={featuresError}
              // Pinned to this company, so reconnecting one client cannot
              // change what the others were given.
              connectHref={
                state.orgId === null ? connectUrl() : connectUrl({ orgId: state.orgId })
              }
              onSetFeatures={(change) => void onSetFeatures(change)}
              onBack={() => dispatch({ type: 'go', screen: 'conns' })}
            />
          )}
            </>
          )}
        </div>

        {state.glance && (
          <GlanceDrawer
            target={state.glance}
            t={t}
            onClose={() => dispatch({ type: 'closeGlance' })}
          />
        )}
      </main>
    </div>
  );
}

function crumbRootFor(
  screen: string,
  orgName: string,
  lists: Record<string, ListFixture>,
  t: (s: string) => string,
): string {
  const listKey = listKeyOf(screen);
  if (listKey && lists[listKey]) return `${orgName} · ${t(lists[listKey]!.root)}`;
  const formKind = formKindOf(screen);
  if (formKind) return `${orgName} · ${t(FORMS[formKind].root)}`;
  if (screen === 'payment') return orgName;
  if (screen === 'summary') return orgName;
  if (reportKindOf(screen)) return `${orgName} · ${t('Reports')}`;
  if (trainingViewOf(screen)) return `${orgName} · ${t('Training')}`;
  if (screen === 'branding') return `${orgName} · ${t('Admin')}`;
  if (screen === 'methods') return `${orgName} · ${t('Admin')}`;
  if (screen === 'people') return `${orgName} · ${t('Admin')}`;
  if (screen === 'conns') return t('Admin');
  if (screen === 'xero') return `${orgName} · ${t('Admin')}`;
  return orgName;
}

function crumbLeafFor(
  screen: string,
  lists: Record<string, ListFixture>,
  t: (s: string) => string,
): string {
  const listKey = listKeyOf(screen);
  if (listKey && lists[listKey]) return t(lists[listKey]!.title);
  const formKind = formKindOf(screen);
  if (formKind) return t(FORMS[formKind].title);
  if (screen === 'queue') return t('Push queue');
  if (screen === 'log') return t('Push log');
  if (screen === 'payment') return t('Money in and out');
  if (screen === 'summary') return t('Sales and purchases');
  const reportKind = reportKindOf(screen);
  if (reportKind) return t(REPORT_TITLES[reportKind]);
  const trainingView = trainingViewOf(screen);
  if (trainingView?.view === 'courses') return t('Courses');
  if (trainingView?.view === 'setup') return t('Training setup');
  if (trainingView?.view === 'deliveries') return t('Training deliveries');
  // The cohort's own name is not known here, and inventing one would be
  // worse than the general word. The page itself says which it is.
  if (trainingView?.view === 'delivery') return t('Training delivery');
  if (screen === 'branding') return t('Branding');
  if (screen === 'methods') return t('Payment methods');
  if (screen.startsWith('import:')) return t('Import contacts');
  if (screen === 'people') return t('People');
  if (screen === 'conns') return t('Client connections');
  if (screen === 'xero') return t('Xero connection');
  return t('Dashboard');
}
